import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { Box, FormControl, IconButton, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import React, { type CSSProperties, type MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { useNurseTimeOff } from "../../../../hooks/useNurseTimeOff";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import { useScheduleDayOf } from "../../../../hooks/useScheduleDayOf";
import { type ScheduleWeeks, useScheduleWeeks } from "../../../../hooks/useScheduleWeeks";
import { INK, STATUS } from "../../../../theme/tokens";
import type { StaffingIssue } from "../../../../utils/autoStaffing";
import { formatPersonFullName } from "../../../../utils/formatters";
import {
	NotificationType,
	type NurseData,
	NurseFullRole,
	NurseFullRoleShort,
	NurseRole,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
} from "../../../../utils/models";
import { generateShiftData } from "../../../../utils/nursePanelUtils";
import { dayIndexOfDate, weekIndexOfDate } from "../../../../utils/panelWeeks";
import { getEmploymentTypeDisplayName, getUniqueLocations } from "../../../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../../../utils/warningDialogs/useStaffingTargetCheck";
import { useNurseWarnings, WarningDialog } from "../../../../utils/warningDialogs/WarningDialog";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { InitiateRoboCallButton } from "../../../InitiateRoboCallButton/InitiateRoboCallButton";
import { useRequestUtils } from "../../../RequestData/Provider/useRequestUtils";
import { MessageWorkingFilterEnum } from "../../../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { PanelDayFallback } from "../../../StaffingChanges/PanelDayFallback";
import { SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { useRequestApproval } from "../../hooks/useRequestApproval";
import { MinimizedNurseDataTable } from "./MinimizedNurseDataTable";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	issue: StaffingIssue;
	onSubmit: (e: React.MouseEvent<HTMLElement>, suggestionIndex: number, nurseId?: number, location?: string) => void;
	onCancel: MouseEventHandler<HTMLButtonElement>;
	isLoading?: boolean;
	onDenyNurse?: (nurseId: number) => void;
	onRestoreNurse?: (nurseId: number) => void;
	queuedDenyNurseIds?: number[];
};

const maxTableHeight: CSSProperties["maxHeight"] = "200px";

export const MinimizedRequestApproval = (props: Props) => {
	const scheduleDayOf = useScheduleDayOf();
	// A request's date is its shift's start; its schedule day is the day that start belongs to.
	const requestDay = scheduleDayOf({ startTime: props.request.message.shift_start, shiftType: props.request.message.shift_type });
	const schedule = useScheduleWeeks(requestDay.date);
	if (schedule.isLoading || schedule.isError) return <PanelDayFallback panelDay={schedule} dataTestId="minimized-request-approval" />;
	return <MinimizedRequestApprovalForWeeks {...props} weeks={schedule.weeks} slotOf={schedule.slotOf} requestDate={requestDay.date} />;
};

const MinimizedRequestApprovalForWeeks = ({
	request,
	issue,
	onSubmit,
	onCancel,
	isLoading,
	onDenyNurse,
	onRestoreNurse,
	queuedDenyNurseIds,
	weeks,
	slotOf,
	requestDate,
}: Props & Pick<ScheduleWeeks, "weeks" | "slotOf"> & { requestDate: DateTime }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isSnf, isMedicalDepartment } = useDepartmentFlags();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const locationsMap = useMemo(() => new Map(chosenDepartment.locations.map((location) => [location.id, location])), [chosenDepartment.locations]);
	const weekIndex = weekIndexOfDate(weeks, requestDate);
	const dayIndex = dayIndexOfDate(weeks, requestDate);
	const lastNameFirst = useLastNameFirst();
	const { data: requestApprovalList, isFetching } = useRequestApproval({
		departmentId: chosenDepartment.department_id,
		requestId: request.id,
	});
	const queuedDenyIdSet = useMemo(() => new Set(queuedDenyNurseIds ?? []), [queuedDenyNurseIds]);
	const hasQueuedDenies = queuedDenyIdSet.size > 0;
	const [selectedNurseId, setSelectedNurseId] = useState<number | undefined>();
	const [selectedRoom, setSelectedRoom] = useState<string>(request.location || "");
	const rooms = useMemo(() => getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location), [chosenDepartment]);

	// Submit args parked while the staffing-target warning is shown; a ref so onComplete (which runs
	// synchronously within checkWarnings) reads the latest value rather than a stale closure.
	const pendingSubmitRef = useRef<{ event: React.MouseEvent<HTMLElement>; suggestionIndex: number; nurseId?: number; location?: string } | null>(null);
	const getTargetCheck = useStaffingTargetCheck();
	const performSubmit = useCallback(() => {
		const pending = pendingSubmitRef.current;
		pendingSubmitRef.current = null;
		if (!pending) return;
		onSubmit(pending.event, pending.suggestionIndex, pending.nurseId, pending.location);
	}, [onSubmit]);
	const [warningState, checkWarnings, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings, , handleTargetConfirm] =
		useNurseWarnings(performSubmit, chosenDepartment.longShiftDuration);

	const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;

	const replaceNurseLocation = useMemo(() => {
		if (!isReplaceRequest) return undefined;
		const requestSlot = slotOf(requestDate, request.message.shift_type);
		if (!requestSlot) return undefined;
		return weeks[requestSlot.weekIndex].weeklyShifts[requestSlot.shiftIndex].days[requestSlot.dayIndex].nurses.find(
			(n) => n.nurseId === (request as ReplaceShiftRequest).replacement_nurse_id,
		)?.location;
	}, [isReplaceRequest, request, weeks, slotOf, requestDate]);

	useEffect(() => {
		const matched = rooms.find((r) => r.toLowerCase() === (replaceNurseLocation ?? "").toLowerCase()) ?? "";
		setSelectedRoom(request.location || matched);
	}, [rooms, replaceNurseLocation]);
	const { isPartialShift } = useRequestUtils();
	const timeOffOf = useNurseTimeOff();

	const nursesDataFromBackend = useMemo(() => {
		return (requestApprovalList || []).map<NurseData>((nurseData) => {
			const currentRequestData = request.message.request_data.find((data) => data.nurse_id === nurseData.id);
			const nurse = getNurseById(nurseData.id);
			if (!nurse) {
				return {
					...nurseData,
					accepted_start_time: currentRequestData?.accepted_start_time ?? nurseData.accepted_start_time,
					accepted_end_time: currentRequestData?.accepted_end_time ?? nurseData.accepted_end_time,
				};
			}
			return {
				...nurseData,
				shifts: generateShiftData({
					nurse: nurse,
					timeOff: timeOffOf(nurse.id),
					weekIndex,
					hospitalData: { weeks },
					targetDayIndex: dayIndex,
					sourceDayIndex: dayIndex,
					chosenDepartment,
					isSnf,
					locationsMap,
				}),
				nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
				...(nurse.nursingStartingDate && {
					nurseStartDate: nurse.nursingStartingDate,
				}),
				selectable: true,
				name: formatPersonFullName(nurse, lastNameFirst),
				bonus: isPartialShift(request, nurseData.id) ? 0 : (request.current_bonus ?? request.bonus ?? 0),
				accepted_start_time: currentRequestData?.accepted_start_time ?? nurseData.accepted_start_time,
				accepted_end_time: currentRequestData?.accepted_end_time ?? nurseData.accepted_end_time,
			};
		});
	}, [requestApprovalList, getNurseById, timeOffOf, weekIndex, dayIndex, request, weeks, chosenDepartment, isSnf, locationsMap, isPartialShift, lastNameFirst]);

	useEffect(() => {
		if (selectedNurseId && queuedDenyIdSet.has(selectedNurseId)) {
			setSelectedNurseId(undefined);
		}
	}, [selectedNurseId, queuedDenyIdSet]);

	const handleRowClick = useCallback(
		(nurseId: number) => {
			setSelectedNurseId(nurseId === selectedNurseId ? undefined : nurseId);
		},
		[selectedNurseId],
	);

	const [showBroadcastMessagePopup, setShowBroadcastMessagePopup] = useState(false);

	const toggleShowBroadcastMessagePopup = useCallback(() => {
		setShowBroadcastMessagePopup((prevState) => !prevState);
	}, []);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				px: 2,
				py: 1,
				gap: 1,
				justifyContent: "space-between",
				minHeight: "300px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}
			>
				<Box
					sx={{
						width: "100%",
						overflow: "clip",
						overflowY: "auto",
					}}
				>
					<MinimizedNurseDataTable
						request={request}
						nurseData={nursesDataFromBackend}
						onRowClick={handleRowClick}
						selectedNurseId={selectedNurseId}
						maxTableHeight={maxTableHeight}
						onDenyNurse={onDenyNurse}
						onRestoreNurse={onRestoreNurse}
						queuedDenyNurseIds={queuedDenyNurseIds}
						loading={isFetching}
						title={
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										fontWeight: 600,
										fontSize: "14px",
										lineHeight: "14px",
										color: INK.SECONDARY,
									}}
								>
									{request.message.shift_start.toFormat(isMedicalDepartment ? "ccc, MMM d" : "ccc, MMM d | h:mma")}
									{!isMedicalDepartment && request.message.shift_end.toFormat(" - h:mma")}
									{isMedicalDepartment && ` | ${request.location}`}
								</Typography>
								<InitiateRoboCallButton requestId={request.id} iconSizeOverride={"small"} date={request.message.shift_start} />
								{!isMedicalDepartment && (
									<InHouseTooltip title={"Message relevant staff (that have not accepted yet)"}>
										<IconButton size={"medium"} onClick={toggleShowBroadcastMessagePopup}>
											<ChatOutlinedIcon fontSize={"small"} />
										</IconButton>
									</InHouseTooltip>
								)}
							</Box>
						}
					/>
				</Box>
			</Box>
			{selectedNurseId &&
				isPartialShift(request, selectedNurseId) &&
				(() => {
					const selectedNurse = nursesDataFromBackend.find((n) => n.id === selectedNurseId);
					return selectedNurse?.accepted_start_time && selectedNurse?.accepted_end_time ? (
						<Box sx={{ p: 1, backgroundColor: STATUS.ERROR.light, borderRadius: 1, border: `1px solid ${STATUS.ERROR.main}`, mb: 1 }}>
							<Typography variant="body2" sx={{ color: STATUS.ERROR.main, fontWeight: 600, fontSize: "0.85rem" }}>
								⚠️ Partial shift: {selectedNurse.accepted_start_time.toFormat("h:mm a")} - {selectedNurse.accepted_end_time.toFormat("h:mm a")}
							</Typography>
						</Box>
					) : null;
				})()}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 2,
				}}
			>
				{rooms.length > 0 && !isMedicalDepartment && (
					<FormControl size="small" sx={{ width: "180px" }}>
						<SystemDropdown
							label="Location"
							items={rooms}
							selectedItem={selectedRoom}
							onChange={(value) => setSelectedRoom(value ?? "")}
							clearable={!isReplaceRequest}
							disabled={isReplaceRequest}
						/>
					</FormControl>
				)}
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						ml: "auto",
					}}
				>
					<SystemButton buttonVariant={"borderless"} onClick={onCancel}>
						Cancel
					</SystemButton>
					<SystemButton
						sx={{
							width: "100px",
						}}
						disabled={selectedNurseId === undefined && !hasQueuedDenies}
						loading={isLoading}
						onClick={(event) => {
							const location = selectedRoom || undefined;
							if (selectedNurseId) {
								pendingSubmitRef.current = {
									event,
									suggestionIndex: issue.suggestions.findIndex((suggestion) => suggestion.otherNurseId === selectedNurseId),
									nurseId: selectedNurseId,
									location,
								};
								// DEFAULT: -1 when the week has no row for the request's type: that shift has no staffing target that
								// day, so the check finds nothing to exceed, as before.
								const shiftIndex = weeks[weekIndex]?.weeklyShifts.findIndex((row) => row.type === request.message.shift_type) ?? -1;
								const targetWarning = getTargetCheck({
									weeks,
									locationName: location,
									weekIndex,
									dayNo: dayIndex,
									shiftIndex,
									isActual: false,
								});
								checkWarnings(false, undefined, undefined, undefined, false, false, targetWarning);
							} else if (hasQueuedDenies) {
								// Deny-only submit: parent's onSubmit flushes the queue without an approval (no target check — nothing is added).
								onSubmit(event, 0, undefined, location);
							}
						}}
					>
						Submit
					</SystemButton>
				</Box>
			</Box>
			{showBroadcastMessagePopup && (
				<NewBroadcastMessageModal
					initialSmsMessage={getShiftPickupMessage(issue.shiftTimes?.date, issue.shiftTimes?.start, issue.shiftTimes?.end)}
					addAppLink={true}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
					nurseIdsToIgnore={issue.suggestions.flatMap((s) => (s.otherNurseId ? [s.nurseId, s.otherNurseId] : [s.nurseId]))}
					jobId={request.job_id}
					initialRolesToSelect={request.message.roles
						.map((r) => NurseRole[NurseFullRoleShort[r as keyof typeof NurseFullRoleShort]])
						?.map((r) => NurseFullRole[r as keyof typeof NurseFullRole])}
					onClose={toggleShowBroadcastMessagePopup}
					initialFilters={
						issue.shiftTimes
							? [
									{
										workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
										dateFilter: issue.shiftTimes.date,
										timeFilter: {
											start: issue.shiftTimes.start,
											end: issue.shiftTimes.end,
										},
									},
								]
							: undefined
					}
				/>
			)}
			<WarningDialog
				openTargetWarning={warningState.isTargetWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelTarget={handleCancelWarnings}
				onConfirmTarget={handleTargetConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
		</Box>
	);
};
